import { Box, Typography } from '@mui/material';
import type { ReactNode } from 'react';
import { useIntl } from 'react-intl';
import type {
    ClinicalTagDto,
    PatientSummaryDto,
} from '~/backend/connector-next-api/connector-next-schemas';
import { SeparatorDot } from '~/components/Separated';
import SensitiveInfoTypography from '~/components/SensitiveInfoTypography';
import { ageInYears, formatPatientName, sexAgeText } from '../model';
import PatientIdText from './PatientIdText';
import TagChips from './TagChips';

/** A secondary line of a patient row: small, muted, one line. */
export const SECONDARY_LINE_SX = {
    display: 'flex',
    alignItems: 'center',
    gap: 0.75,
    minWidth: 0,
    fontSize: 12,
    color: 'text.secondary',
    whiteSpace: 'nowrap',
} as const;

/** The secondary line under a patient's name, in monospace. */
export const PATIENT_META_LINE_SX = {
    ...SECONDARY_LINE_SX,
    fontFamily: 'fontFamilyMonospace',
} as const;

type PatientIdentityProps = {
    patient: Pick<
        PatientSummaryDto,
        'dicomPatientName' | 'dicomPatientId' | 'sex' | 'birthDate' | 'tagIds'
    >;
    /** The day the age is counted to. */
    today: Date;
    /** Every clinical tag, which name and colour the patient's; undefined until they have loaded. */
    tags: readonly ClinicalTagDto[] | undefined;
    /** Further lines under the tags. */
    children?: ReactNode;
};

/** Who a patient is: the name, the patient ID with sex and age, then the patient's tags. */
export default function PatientIdentity({
    patient,
    today,
    tags,
    children,
}: PatientIdentityProps) {
    const intl = useIntl();
    const sexAge = sexAgeText(
        intl,
        patient.sex,
        ageInYears(patient.birthDate, today)
    );
    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: '3px',
                minWidth: 0,
            }}
        >
            <Typography
                component="span"
                noWrap
                sx={{
                    display: 'block',
                    minWidth: 0,
                    fontSize: 15,
                    fontWeight: 600,
                    lineHeight: 1.25,
                    color: 'text.primary',
                }}
            >
                <SensitiveInfoTypography>
                    {formatPatientName(patient.dicomPatientName) || '-'}
                </SensitiveInfoTypography>
            </Typography>
            <Box sx={PATIENT_META_LINE_SX}>
                <PatientIdText
                    id={patient.dicomPatientId}
                    tooltipPlacement="right"
                />
                {sexAge && (
                    <>
                        <SeparatorDot />
                        <SensitiveInfoTypography>
                            {sexAge}
                        </SensitiveInfoTypography>
                    </>
                )}
            </Box>
            <TagChips tagIds={patient.tagIds ?? []} tags={tags} />
            {children}
        </Box>
    );
}
